'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { debounce, isEmpty } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import text from 'I18n/utils/unescapedText';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import HublDocsItem from 'DesignComponents/hublDocs/HublDocsItem';
import UIAccordianItem from 'UIComponents/accordion/UIAccordionItem';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UILink from 'UIComponents/link/UILink';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UISelect from 'UIComponents/input/UISelect';
import * as HublDocsActions from 'DesignData/actions/HublDocs';
import * as hublDocsSelectors from 'DesignData/selectors/hublDocs';
import { SECTIONS_ORDER, PUBLIC_DOCS_LINK_BASE } from 'DesignData/constants/HublReference';
import { createSearchRegExp, hasSearchMatch } from 'DesignData/lib/text';

const i18nKey = key => `DesignEditorsUI.statusBar.header.reference.hublDocs.${key}`;

const mapStateToProps = state => {
  return {
    hublDocs: hublDocsSelectors.getHublDocs(state)
  };
};

const mapDispatchToProps = {
  fetchDocs: HublDocsActions.fetch
};

class HublDocsPanel extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      searchTerm: '',
      selectedFilter: ''
    };

    this.filterDocs = (hublDocs, searchTerm, selectedFilter) => {
      if (!hublDocs) return null;
      const searchRegex = createSearchRegExp(searchTerm) || null;
      const filteredDocs = {};
      SECTIONS_ORDER.forEach(sectionKey => {
        const sectionDocs = hublDocs[sectionKey];

        if (!(selectedFilter.length && selectedFilter !== sectionKey)) {
          filteredDocs[sectionKey] = Object.keys(sectionDocs).reduce((acc, item) => {
            const docItem = sectionDocs[item];

            if (hasSearchMatch(searchRegex, docItem.name)) {
              acc[item] = docItem;
            }

            return acc;
          }, {});
        }
      });
      return filteredDocs;
    };

    this.debouncedSearchInput = debounce(searchTerm => {
      this.setState({
        searchTerm
      });
    }, 200);

    this.onSearchInput = e => {
      this.debouncedSearchInput(e.target.value);
    };

    this.onFilterChange = e => {
      this.setState({
        selectedFilter: e.target.value
      });
    };
  }

  componentDidMount() {
    if (!this.props.hublDocs) {
      this.props.fetchDocs();
    }
  }

  renderSearchSection() {
    return /*#__PURE__*/_jsxs("div", {
      className: "hubl-docs-search-container",
      children: [/*#__PURE__*/_jsx(UILink, {
        className: "full-hubl-doc-link",
        href: `${PUBLIC_DOCS_LINK_BASE}/intro-to-hubl`,
        external: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('docsLink')
        })
      }), /*#__PURE__*/_jsx(UISearchInput, {
        onChange: this.onSearchInput
      })]
    });
  }

  renderFilters() {
    const filterPlaceholder = text(i18nKey('filter'));
    const filterOptions = []; // Add all syntax filter

    filterOptions.push({
      text: filterPlaceholder,
      value: ''
    });
    SECTIONS_ORDER.forEach(section => {
      const sectionName = text(i18nKey(`sections.${section}`));
      filterOptions.push({
        text: sectionName,
        value: section
      });
    });
    return /*#__PURE__*/_jsx(UISelect, {
      placement: "bottom left",
      buttonUse: "link",
      options: filterOptions,
      placeholder: filterPlaceholder,
      onChange: this.onFilterChange
    });
  }

  renderItems(sectionDocs) {
    return Object.keys(sectionDocs).map(item => {
      return /*#__PURE__*/_jsx(HublDocsItem, {
        item: sectionDocs[item]
      }, item);
    });
  }

  renderDocs(filteredDocs) {
    let totalDocs = 0;
    if (!filteredDocs) return /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true
    });
    Object.keys(filteredDocs).forEach(sectionKey => {
      totalDocs += Object.keys(filteredDocs[sectionKey]).length;
    });

    if (totalDocs === 0) {
      return /*#__PURE__*/_jsx("div", {
        className: "empty-message",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: i18nKey('empty')
        })
      });
    }

    return SECTIONS_ORDER.map(sectionKey => {
      const sectionDocs = filteredDocs[sectionKey];

      if (!sectionDocs || isEmpty(sectionDocs)) {
        return null;
      }

      return /*#__PURE__*/_jsx(UIAccordianItem, {
        title: /*#__PURE__*/_jsx("h4", {
          children: text(i18nKey(`sections.${sectionKey}`))
        }),
        defaultOpen: true,
        children: this.renderItems(sectionDocs)
      }, sectionKey);
    });
  }

  render() {
    const {
      hublDocs
    } = this.props;
    const {
      searchTerm,
      selectedFilter
    } = this.state;
    const filteredDocs = this.filterDocs(hublDocs, searchTerm, selectedFilter);
    const renderedSearchSection = this.renderSearchSection();
    const renderedFilters = this.renderFilters();
    const renderedContent = this.renderDocs(filteredDocs);
    return /*#__PURE__*/_jsxs(UIPanel, {
      panelClassName: "p-bottom-0",
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: this.props.toggleHublDocPanel
      }), /*#__PURE__*/_jsx(UIPanelHeader, {
        children: /*#__PURE__*/_jsx("h4", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('header')
          })
        })
      }), /*#__PURE__*/_jsxs(UIPanelBody, {
        children: [/*#__PURE__*/_jsx(UIPanelSection, {
          className: "m-bottom-0",
          children: renderedSearchSection
        }), /*#__PURE__*/_jsx(UIPanelSection, {
          className: "hubl-docs-filters",
          children: renderedFilters
        }), /*#__PURE__*/_jsx(UIPanelSection, {
          className: "p-top-0",
          children: renderedContent
        })]
      })]
    });
  }

}

HublDocsPanel.propTypes = {
  toggleHublDocPanel: PropTypes.func.isRequired,
  fetchDocs: PropTypes.func.isRequired,
  hublDocs: PropTypes.object
};
export default connect(mapStateToProps, mapDispatchToProps)(HublDocsPanel);